/**
 * MyProfile Page Facade (100% Shared)
 * 
 * Composes all necessary facades for the profile page.
 * Uses dependency injection pattern to remain platform-agnostic.
 * 
 * This facade is 100% shared between web and plugin.
 * Platform-specific implementations (navigation, auth, logout) are injected.
 * 
 * Pattern: Clean composition - delegates to smaller facades, no inline business logic.
 * Reference: useLandingPageFacade
 * 
 * Usage (Web):
 * ```tsx
 * const navigation = useWebNavigation();
 * const auth = useAuthDialog();
 * const logout = useWebLogout();
 * const facade = useMyProfilePageFacade({ 
 *   navigation, 
 *   auth,
 *   logout,
 *   showToast 
 * });
 * ```
 * 
 * Usage (Plugin):
 * ```tsx
 * const navigation = usePluginNavigation();
 * const auth = usePairingCodeAuth();
 * const logout = usePluginLogout();
 * const facade = useMyProfilePageFacade({ 
 *   navigation, 
 *   auth,
 *   logout,
 *   showToast 
 * });
 * ```
 * 
 * @module useMyProfilePageFacade
 * @category Facades/Pages
 */

import { useMemo } from 'react';
import { useHeaderConfig } from '../../useHeaderConfig';
import { useProfileFacade } from '../shared';
import { useSidebarConfig, type SidebarConfig } from '../shared/useSidebarConfig';
import { useSubscriptionExpiration, type SubscriptionExpirationState } from '../shared/useSubscriptionExpiration';
import type { NavigationFacade, ShowToastFn, AuthFacade } from '../shared/types';

export interface LogoutFacade {
  /**
   * Logout function that handles platform-specific cleanup
   */
  logout: () => Promise<void>;
}

export interface MyProfilePageFacadeParams {
  /**
   * Platform-specific navigation implementation
   */
  navigation: NavigationFacade;

  /**
   * Platform-specific auth implementation
   */
  auth: AuthFacade;

  /**
   * Platform-specific logout implementation
   */
  logout: LogoutFacade;

  /**
   * Platform-specific toast notification function
   */
  showToast: ShowToastFn;

  /**
   * Optional: Handler for subscription renewal
   */
  onRenewSubscription?: () => void;
}

export interface MyProfilePageFacade {
  // ========== State ==========
  state: {
    isLoggedIn: boolean;
    user: any | null;
  };

  // ========== Header ==========
  header: ReturnType<typeof useHeaderConfig>;

  // ========== Navigation ==========
  navigation: NavigationFacade;

  // ========== Profile ==========
  profile: ReturnType<typeof useProfileFacade>;

  // ========== Auth ==========
  auth: AuthFacade;

  // ========== Logout ==========
  logout: LogoutFacade;

  // ========== Sidebar ==========
  sidebar: SidebarConfig;

  // ========== Subscription Expiration ==========
  subscriptionExpiration: SubscriptionExpirationState;

  // ========== Renewal Action ==========
  onRenewSubscription: () => void;
}

/**
 * MyProfile Page Facade Hook
 * 
 * Composes all necessary facades for the profile page.
 * 100% shared between web and plugin through dependency injection.
 */
export function useMyProfilePageFacade({
  navigation,
  auth,
  logout,
  showToast,
  onRenewSubscription = () => {
    showToast({ message: 'Redirecting to Stripe checkout...', severity: 'info' });
  },
}: MyProfilePageFacadeParams): MyProfilePageFacade {
  // ========== Reusable Facades ==========
  const profile = useProfileFacade(showToast);

  // ========== Header Config ==========
  const headerConfig = useHeaderConfig({
    onNavigateToProfile: () => {}, // Already on profile page
    onNavigateToBilling: navigation.toBilling,
    onNavigateToAppSettings: navigation.toAppSettings,
    onNavigateToIntegrations: navigation.toIntegrations,
    onNavigateToReport: navigation.toReportProblem,
    onLoginClick: auth.openLogin,
    onRegisterClick: auth.openRegister,
    showToast,
    currentPath: '/profile',
  });

  // ========== Sidebar Config ==========
  const sidebar = useSidebarConfig({
    pageType: 'standard',
    isLoggedIn: headerConfig.isLoggedIn,
    onResetConversation: () => {}, // No conversation reset on profile page
    onNavigateToLanding: navigation.toLanding,
    onNavigateToProjectsList: navigation.toProjectsList,
    currentPath: '/profile',
    onModeChange: ()=>{}
  });

  // ========== Subscription Expiration ==========
  // Reads from Redux: planDetails.subscriptionInfo.planEndDate, planDetails.name
  const subscriptionExpiration = useSubscriptionExpiration();

  // ========== Return Composed Facade ==========
  return useMemo(
    () => ({
      state: {
        isLoggedIn: headerConfig.isLoggedIn,
        user: profile.user,
      },
      header: headerConfig,
      navigation,
      profile,
      auth,
      logout,
      sidebar,
      subscriptionExpiration,
      onRenewSubscription,
    }),
    [headerConfig, navigation, profile, auth, logout, sidebar, subscriptionExpiration, onRenewSubscription]
  );
}
